/**
 * EmployeeSignatureFormPage — iter-218c (Feb 2026)
 *
 * Add or edit an employee's signature. On CREATE mode a searchable
 * dropdown lists admin platform users (from `GET /admin/users?platform=asb606`).
 * On EDIT mode the user_id is locked (path param) and the form pre-fills.
 *
 * Fields:
 *   • Employee dropdown       — auto-fills user_email + user_display_name
 *   • employee_name_ar        — Arabic-only keystroke filter (required)
 *   • employee_name_en        — English-only keystroke filter (required)
 *   • job_title_ar            — optional
 *   • job_title_en            — optional
 *   • signature image upload  — ≤600 KB, previewed inline
 *
 * Routes:
 *   /control-panel-settings/company-mgmt/employees/signature/new
 *   /control-panel-settings/company-mgmt/employees/signature/:userId
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  PenLine, Upload, Save, Loader2, User as UserIcon, ChevronLeft, ChevronRight,
  ImagePlus, Check,
} from 'lucide-react';

import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_SIZE = 600 * 1024;

export default function EmployeeSignatureFormPage() {
  const navigate = useNavigate();
  const { userId: routeUserId } = useParams();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const Arrow = isAr ? ChevronLeft : ChevronRight;
  const fileRef = useRef(null);

  const isEdit = !!routeUserId && routeUserId !== 'new';

  // Employee list (admin platform users)
  const [employees, setEmployees] = useState([]);
  const [empLoading, setEmpLoading] = useState(true);

  // Form state
  const [form, setForm] = useState({
    user_id: '', user_email: '', user_display_name: '',
    employee_name_ar: '', employee_name_en: '',
    job_title_ar: '', job_title_en: '',
    signature_data_url: '',
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving]   = useState(false);
  const [empDropdownOpen, setEmpDropdownOpen] = useState(false);

  // Load admin platform users for the dropdown
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/admin/users`, {
          headers: getAuthHeaders(),
          params: { platform: 'asb606' },
        });
        setEmployees(r.data?.data || []);
      } catch {
        toast.error(t('فشل تحميل قائمة الموظفين', 'Failed to load employees'));
      } finally {
        setEmpLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  // In EDIT mode, load the existing signature
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/api/admin/certificates/employee-signatures/${routeUserId}`,
          { headers: getAuthHeaders() },
        );
        setForm({
          user_id:            r.data.user_id || routeUserId,
          user_email:         r.data.user_email || '',
          user_display_name:  r.data.user_display_name || '',
          employee_name_ar:   r.data.employee_name_ar || '',
          employee_name_en:   r.data.employee_name_en || '',
          job_title_ar:       r.data.job_title_ar || '',
          job_title_en:       r.data.job_title_en || '',
          signature_data_url: r.data.signature_data_url || '',
        });
      } catch (e) {
        toast.error(e.response?.data?.detail || t('فشل تحميل التوقيع', 'Failed to load signature'));
        navigate('/control-panel-settings/company-mgmt/employees/signature', { replace: true });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [routeUserId, isEdit]);

  const selectedEmp = useMemo(
    () => employees.find((e) => e.id === form.user_id),
    [employees, form.user_id],
  );

  const pickEmployee = async (emp) => {
    // Optimistic prefill from the picker list (name/email)
    setForm((f) => ({
      ...f,
      user_id:            emp.id,
      user_email:         emp.email || '',
      user_display_name:  emp.name || emp.username || emp.email || '',
    }));
    setEmpDropdownOpen(false);

    // iter-218d (Feb 2026) — Auto-populate AR/EN name + job title from the
    // employee's own profile (merged with the supervisor record via the
    // /employee-lookup endpoint). Keeps whatever the admin has typed if the
    // lookup fails or returns empty strings.
    try {
      const r = await axios.get(
        `${API}/api/admin/certificates/employee-lookup/${emp.id}`,
        { headers: getAuthHeaders() },
      );
      const d = r.data || {};
      setForm((f) => ({
        ...f,
        employee_name_ar: f.employee_name_ar || filterAr(d.employee_name_ar || ''),
        employee_name_en: f.employee_name_en || filterEn(d.employee_name_en || ''),
        job_title_ar:     f.job_title_ar     || filterAr(d.job_title_ar || ''),
        job_title_en:     f.job_title_en     || filterEn(d.job_title_en || ''),
      }));
    } catch {
      /* silent — user can fill the fields manually */
    }
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('اختر صورة فقط', 'Please pick an image file'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      if (dataUrl.length > MAX_SIZE) {
        toast.error(t(
          'الصورة كبيرة جداً — الحد الأقصى 600 كيلوبايت.',
          'Image too large — max 600 KB. Compress first.',
        ));
        return;
      }
      setForm((f) => ({ ...f, signature_data_url: dataUrl }));
    };
    reader.readAsDataURL(file);
  };

  // iter-218f (Feb 2026) — Relaxed input filters at user's request:
  // both AR and EN fields accept Arabic + English letters + digits +
  // common punctuation (comma, dot, dash, ampersand, "and", parentheses,
  // slash, apostrophe, at, plus, colon). Only truly dangerous / control
  // characters are stripped.
  const filterAr = (v) => v.replace(/[\u0000-\u001F\u007F<>]/g, '');
  const filterEn = (v) => v.replace(/[\u0000-\u001F\u007F<>]/g, '');

  const save = async () => {
    if (!form.user_id) {
      toast.error(t('اختر الموظف أولاً', 'Please pick an employee first'));
      return;
    }
    if (!form.employee_name_ar.trim()) {
      toast.error(t('أدخل الاسم بالعربية', 'Enter the Arabic name'));
      return;
    }
    if (!form.employee_name_en.trim()) {
      toast.error(t('أدخل الاسم بالإنجليزية', 'Enter the English name'));
      return;
    }
    if (!form.signature_data_url) {
      toast.error(t('ارفع صورة التوقيع', 'Upload the signature image'));
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/admin/certificates/employee-signatures`,
        form,
        { headers: getAuthHeaders() },
      );
      toast.success(t('تم الحفظ', 'Saved'));
      navigate('/control-panel-settings/company-mgmt/employees/signature');
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout
      title={isEdit ? 'تعديل توقيع موظف' : 'إضافة توقيع لموظف'}
      titleEn={isEdit ? 'Edit Employee Signature' : 'Add Employee Signature'}
      subtitle="اختر الموظف وارفع توقيعه — يُستخدم عند اعتماد الشهادات"
      subtitleEn="Pick the employee and upload their signature — used on approved certificates"
      screenCode="EMP-SIG-FORM"
      backPath="/control-panel-settings/company-mgmt/employees/signature"
      onBack={() => navigate('/control-panel-settings/company-mgmt/employees/signature')}
      breadcrumb={[
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'ASB606 ADMIN (للتحكم)', labelEn: 'ASB606 ADMIN (Control)', path: '/control-panel-settings' },
        { labelAr: 'التحكم بإدارة الشركة', labelEn: 'Company Management Control', path: '/control-panel-settings/company-mgmt' },
        { labelAr: 'إعدادات الموظفين', labelEn: 'Employees Settings', path: '/control-panel-settings/company-mgmt/employees' },
        { labelAr: 'التوقيع', labelEn: 'Signatures', path: '/control-panel-settings/company-mgmt/employees/signature' },
        { labelAr: isEdit ? 'تعديل' : 'إضافة', labelEn: isEdit ? 'Edit' : 'Add' },
      ]}
    >
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="employee-signature-form">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-white/40" /></div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8 space-y-5"
          >
            {/* Employee picker */}
            <div>
              <label className="block text-[11px] text-white/50 mb-1">
                {t('الموظف', 'Employee')} <span className="text-red-400">*</span>
              </label>
              {isEdit ? (
                <div className="rounded-lg bg-black/40 border border-white/10 p-3 flex items-center gap-2"
                     data-testid="emp-locked">
                  <UserIcon className="w-4 h-4 text-white/40" />
                  <span className="text-white text-sm">
                    {form.user_display_name || form.user_email || form.user_id}
                  </span>
                  <span className="text-[10px] text-white/40 ms-auto">({t('غير قابل للتغيير', 'locked')})</span>
                </div>
              ) : (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setEmpDropdownOpen((v) => !v)}
                    className="w-full text-start bg-black border border-white/10 rounded-lg h-11 px-3 text-white text-sm flex items-center justify-between hover:border-white/20"
                    data-testid="emp-picker-btn"
                  >
                    {selectedEmp ? (
                      <span className="truncate">
                        {selectedEmp.name || selectedEmp.username || selectedEmp.email}
                        {selectedEmp.email && <span className="text-white/40 text-xs ms-2">— {selectedEmp.email}</span>}
                      </span>
                    ) : (
                      <span className="text-white/40">
                        {empLoading ? t('جارٍ التحميل…', 'Loading…') : t('اختر موظفاً…', 'Pick an employee…')}
                      </span>
                    )}
                    <Arrow className="w-4 h-4 opacity-50" />
                  </button>
                  {empDropdownOpen && !empLoading && (
                    <div className="absolute z-30 mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-lg max-h-72 overflow-y-auto shadow-xl"
                         data-testid="emp-picker-list">
                      {employees.length === 0 ? (
                        <div className="p-4 text-sm text-white/40 text-center">
                          {t('لا يوجد موظفين', 'No employees found')}
                        </div>
                      ) : (
                        employees.map((e) => (
                          <button
                            key={e.id}
                            type="button"
                            onClick={() => pickEmployee(e)}
                            className="w-full text-start px-3 py-2.5 text-sm text-white/90 hover:bg-white/[0.04] border-b border-white/5 last:border-0 flex items-center gap-2"
                            data-testid={`emp-picker-option-${e.id}`}
                          >
                            <UserIcon className="w-3.5 h-3.5 text-white/40 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="truncate">{e.name || e.username || e.email}</div>
                              {e.email && <div className="text-[10px] text-white/40 truncate" dir="ltr">{e.email}</div>}
                            </div>
                            {form.user_id === e.id && <Check className="w-4 h-4 text-emerald-400" />}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Name AR + EN */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] text-white/50 mb-1">
                  {t('الاسم بالعربية', 'Name (Arabic)')} <span className="text-red-400">*</span>
                </label>
                <Input
                  value={form.employee_name_ar}
                  onChange={(e) => setForm((f) => ({ ...f, employee_name_ar: filterAr(e.target.value) }))}
                  placeholder={t('الاسم كما يظهر على الشهادة', 'Name as shown on the certificate')}
                  className="bg-black border-white/10 text-white h-10"
                  dir="rtl"
                  data-testid="emp-name-ar"
                />
              </div>
              <div>
                <label className="block text-[11px] text-white/50 mb-1">
                  {t('الاسم بالإنجليزية', 'Name (English)')} <span className="text-red-400">*</span>
                </label>
                <Input
                  value={form.employee_name_en}
                  onChange={(e) => setForm((f) => ({ ...f, employee_name_en: filterEn(e.target.value) }))}
                  placeholder={t('الاسم كما يظهر بالإنجليزية', 'Name as shown in English')}
                  className="bg-black border-white/10 text-white h-10"
                  dir="ltr"
                  data-testid="emp-name-en"
                />
              </div>
            </div>

            {/* Job title AR + EN */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] text-white/50 mb-1">
                  {t('المسمى الوظيفي (عربي)', 'Job title (Arabic)')}
                </label>
                <Input
                  value={form.job_title_ar}
                  onChange={(e) => setForm((f) => ({ ...f, job_title_ar: filterAr(e.target.value) }))}
                  placeholder={t('مثال: المدير التنفيذي', 'e.g. CEO')}
                  className="bg-black border-white/10 text-white h-10"
                  dir="rtl"
                  data-testid="emp-title-ar"
                />
              </div>
              <div>
                <label className="block text-[11px] text-white/50 mb-1">
                  {t('المسمى الوظيفي (إنجليزي)', 'Job title (English)')}
                </label>
                <Input
                  value={form.job_title_en}
                  onChange={(e) => setForm((f) => ({ ...f, job_title_en: filterEn(e.target.value) }))}
                  placeholder={t('e.g. Chief Executive Officer', 'e.g. Chief Executive Officer')}
                  className="bg-black border-white/10 text-white h-10"
                  dir="ltr"
                  data-testid="emp-title-en"
                />
              </div>
            </div>

            {/* Signature upload */}
            <div>
              <label className="block text-[11px] text-white/50 mb-1">
                {t('صورة التوقيع', 'Signature image')} <span className="text-red-400">*</span>
              </label>
              <div className="rounded-xl border-2 border-dashed border-white/10 bg-white/[0.015] p-4 flex flex-col items-center gap-3">
                {form.signature_data_url ? (
                  <img src={form.signature_data_url} alt="Signature preview"
                       className="max-h-[140px] max-w-full object-contain bg-white rounded p-2"
                       data-testid="emp-sig-preview" />
                ) : (
                  <div className="py-6 text-center">
                    <ImagePlus className="w-8 h-8 mx-auto mb-2 text-white/25" />
                    <p className="text-xs text-white/40">{t('لم يتم اختيار صورة', 'No image selected')}</p>
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={onPickFile}
                  data-testid="emp-sig-file-input"
                />
                <Button
                  onClick={() => fileRef.current?.click()}
                  variant="outline"
                  className="border-white/15 bg-white/[0.03] text-white hover:bg-white/[0.06]"
                  data-testid="emp-sig-upload-btn"
                >
                  <Upload className="w-4 h-4 me-1.5" />
                  {form.signature_data_url ? t('استبدال', 'Replace') : t('اختيار صورة', 'Choose file')}
                </Button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-4 border-t border-white/5">
              <Button
                onClick={() => navigate('/control-panel-settings/company-mgmt/employees/signature')}
                variant="outline"
                className="border-white/15 bg-white/[0.03] text-white hover:bg-white/[0.06]"
                data-testid="emp-sig-cancel"
              >
                {t('إلغاء', 'Cancel')}
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                className="ms-auto bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-40"
                data-testid="emp-sig-save"
              >
                {saving ? <Loader2 className="w-4 h-4 me-1.5 animate-spin" /> : <Save className="w-4 h-4 me-1.5" />}
                {t('حفظ التوقيع', 'Save signature')}
              </Button>
            </div>
          </motion.div>
        )}
      </section>
    </DashboardLayout>
  );
}
